import { useEffect, useRef, useState } from 'react';
import termsapi from "./TermsAPI";

/**
 * Hook to maintain a Django session by debouncing client-side activity.
 * @param {number} pingIntervalMs - Minimum time between server pings (Default: 5 mins).
 */
const useSessionHeartbeat = (
    pingIntervalMs = 300000 // 5 minutes in milliseconds
) => {
    const lastPingTime = useRef(0);
    const timeoutRef = useRef(null);
    const [disabled, setDisabled] = useState(false);

    useEffect(() => {
	// Core function that safely pings the Django REST API
	const sendHeartbeat = async () => {
	    await termsapi.updateSession().then(response => {
		lastPingTime.current = Date.now();
	    }).catch(err => {
		setDisabled(true);
		lastPingTime.current = Date.now();
	    });
	}

	// Handler that checks if enough time has passed to trigger a ping
	const handleUserActivity = () => {
	    const now = Date.now();

	    if (disabled) { // user is not logged in 
		return;
	    }

	    // If a ping was sent recently, do nothing and let the user keep working
	    if (now - lastPingTime.current < pingIntervalMs) {
		return;
	    }

	    
	    // Debounce mechanism: Clear any pending scheduled ping
	    if (timeoutRef.current) {
		clearTimeout(timeoutRef.current);
	    }

	    // Schedule the ping. This ensures a flurry of keystrokes/clicks 
	    // only fires a single network request after the user pauses briefly.
	    timeoutRef.current = setTimeout(() => {
		sendHeartbeat();
	    }, 1000); // 1-second debounce delay
	};


	// Monitor universal user interactions
	const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
	
	activityEvents.forEach((event) => {
	    window.addEventListener(event, handleUserActivity);
	});
	
	// Initial ping on component mount to establish the timeline
	sendHeartbeat();
	
	// Clean up event listeners and timers when the component unmounts
	return () => {
	    activityEvents.forEach((event) => {
		window.removeEventListener(event, handleUserActivity);
	    });
	    if (timeoutRef.current) {
		clearTimeout(timeoutRef.current);
	    }
	};
    }, [pingIntervalMs]);
};


// Actively keeps the Django REST session alive during user form interaction
// Fires at most once every 5 minutes while active
//useSessionHeartbeat(300000);

export default useSessionHeartbeat;
